/**
 * @fileoverview Modal wrapper for DmsFileViewer component
 * Provides a ready-to-use modal for viewing files
 */
import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { DmsModal, DmsButton, intl, getToken } from "dmsbasereact";
import { getBaseURL } from "../../build_config";
import { DmsFileViewer } from "./DmsFileViewer";

/**
 * DmsFileViewerModal - Modal wrapper for file viewing
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Close handler
 * @param {number} props.filePk - File primary key
 * @param {string} props.fileName - File name (for type detection)
 * @param {string} props.title - Modal title (optional)
 */
export const DmsFileViewerModal = ({ isOpen, onClose, filePk = null, fileName = "", title = "" }) => {
    const [downloading, setDownloading] = useState(false);

    const handleDownload = useCallback(async () => {
        if (!filePk) return;

        try {
            setDownloading(true);
            const token = getToken();
            const response = await fetch(`${getBaseURL()}/file/${filePk}`, {
                headers: { Authorization: `Bearer ${token?.access_token}` }
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName || `file_${filePk}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Download failed:", err);
        } finally {
            setDownloading(false);
        }
    }, [filePk, fileName]);

    if (!isOpen) return null;

    const displayTitle = title || fileName || intl.get("ViewDocument").d("View Document");

    return (
        <DmsModal
            isVisible={isOpen}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
        >
            <DmsModal.Header onClose={onClose} title={displayTitle} />
            <DmsModal.Body sx={{ minHeight: "70vh", p: 0 }}>
                <DmsFileViewer filePk={filePk} fileName={fileName} />
            </DmsModal.Body>
            <DmsModal.Footer>
                <DmsButton
                    variant="secondary"
                    text={downloading ? intl.get("Downloading").d("Downloading...") : intl.get("Download").d("Download")}
                    onClick={handleDownload}
                    disabled={!filePk || downloading}
                />
                <DmsButton
                    variant="outlined"
                    text={intl.get("Close").d("Close")}
                    onClick={onClose}
                />
            </DmsModal.Footer>
        </DmsModal>
    );
};

DmsFileViewerModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    filePk: PropTypes.number,
    fileName: PropTypes.string,
    title: PropTypes.string
};
